import { normalizeQuantity, quantityError } from './quantity'

/**
 * Цена кассира — строка в рублях, как её ввёл человек («75,50»). Клиент
 * проверяет форму и переводит в копейки для тела запроса. Сумму строки он
 * показывает сразу (`lineAmountKopecks`), а решает всё равно служба: её ответ
 * заменяет предварительную сумму (RP-FB15).
 * Любая цена, без причины (решение владельца 13.10.2026, RP-FB8).
 */
const PRICE = /^\d{1,9}(?:[.,]\d{1,2})?$/

/** Предел поля — 99 999 999,99 ₽: служба допускает больше (`MaxTotalKopecks`), но такую цену кассир не набирает. */
const MAX_PRICE_KOPECKS = 9_999_999_999

/** Почему цена не годится — словами; `null` — годится. */
export function priceError(text: string): string | null {
  const value = text.trim().replace(',', '.')
  if (value === '') return 'Укажите цену'
  if (!PRICE.test(value)) return 'Цена — число в рублях, до двух знаков после запятой'
  const kopecks = priceKopecks(value)
  if (kopecks < 1) return 'Цена должна быть больше нуля'
  if (kopecks > MAX_PRICE_KOPECKS) return 'Слишком большая цена'
  return null
}

/** Копейки для тела запроса. Строка уже проверена: до двух знаков — целые копейки без плавающей точки. */
export function priceKopecks(text: string): number {
  const [whole = '0', fraction = ''] = text.trim().replace(',', '.').split('.')
  return Number.parseInt(whole, 10) * 100 + Number.parseInt(fraction.padEnd(2, '0'), 10)
}

/**
 * Сумма строки в копейках — предварительная, пока служба не ответила: цена × количество,
 * до копейки, середина («.5») — от нуля. Это формула службы (`AqsiSales`: `decimal.Round(
 * priceKopecks * quantity, 0, AwayFromZero)`), и считается она целыми числами, без плавающей
 * точки: количество — до трёх знаков, поэтому цена × тысячные / 1000 с округлением.
 * `null` — количество не годится или сумма вышла за безопасное целое: решает служба.
 */
export function lineAmountKopecks(priceKopecks: number, quantity: string): number | null {
  if (quantityError(quantity, true) !== null || !Number.isSafeInteger(priceKopecks) || priceKopecks < 0) return null
  const [whole = '0', fraction = ''] = normalizeQuantity(quantity).split('.')
  const thousandths = BigInt(whole) * 1000n + BigInt(fraction.padEnd(3, '0'))
  const amount = (BigInt(priceKopecks) * thousandths + 500n) / 1000n
  return amount <= BigInt(Number.MAX_SAFE_INTEGER) ? Number(amount) : null
}

/** Копейки → строка для поля ввода: «75,50», «90,00». */
export function priceInputText(kopecks: number): string {
  return `${Math.trunc(kopecks / 100)},${String(kopecks % 100).padStart(2, '0')}`
}

/** Рублей в поле не больше восьми знаков: 99 999 999,99 ₽. */
const MAX_WHOLE_DIGITS = 8

/**
 * Маска поля цены, как в кассе 1С: в поле всегда «рубли,копейки». Цифры до
 * запятой — рубли («5» → «5,00», затем «9» → «59,00»), копейки по умолчанию
 * «00» и правятся поверх, если поставить курсор за запятую; запятая или
 * точка в рублях переводит курсор на копейки. `raw` и `caret` — поле после
 * ввода, `previous` — что было до него. `null` — ввод не принят, поле остаётся прежним.
 */
export function maskPrice(raw: string, caret: number, previous: string): { text: string; caret: number } | null {
  let text = raw.replace(/\./g, ',')
  let toFraction = false
  const commas = text.split(',').length - 1
  if (commas === 0) {
    // Стёрта сама запятая — запятую не стираем, курсор просто переходит через неё.
    const at = previous.indexOf(',')
    if (at >= 0 && previous.length === raw.length + 1 && previous.slice(0, at) + previous.slice(at + 1) === raw) {
      return { text: previous, caret: at }
    }
    // Пустое или выделенное целиком поле: набранное — рубли.
    text = `${text.slice(0, caret)},${text.slice(caret)}`
  } else if (commas > 1) {
    if (text[caret - 1] !== ',') return null
    text = text.slice(0, caret - 1) + text.slice(caret)
    toFraction = true
  }
  const at = text.indexOf(',')
  const wholeRaw = text.slice(0, at)
  const fractionRaw = text.slice(at + 1)
  const digits = (value: string) => value.replace(/\D/g, '')
  const wholeDigits = digits(wholeRaw)
  const wholeCaret = caret <= at ? digits(wholeRaw.slice(0, caret)).length : null
  const fractionCaret = caret > at ? Math.min(digits(fractionRaw.slice(0, caret - at - 1)).length, 2) : null
  let fraction = digits(fractionRaw)
  if (fraction.length > 2) {
    // Копейки набираются поверх: введённая цифра заменяет следующую.
    const position = fractionCaret ?? 0
    fraction = (fraction.slice(0, position) + fraction.slice(position + 1)).slice(0, 2)
  }
  while (fraction.length < 2) {
    // Стёртая копейка становится нулём на своём месте.
    const position = fractionCaret ?? fraction.length
    fraction = fraction.slice(0, position) + '0' + fraction.slice(position)
  }
  const stripped = wholeDigits.replace(/^0+/, '')
  if (stripped.length > MAX_WHOLE_DIGITS) return null
  const whole = stripped === '' ? '0' : stripped
  const removed = wholeDigits.length - stripped.length
  const nextCaret = toFraction
    ? whole.length + 1
    : wholeCaret !== null
      ? stripped === ''
        ? 1
        : Math.min(Math.max(0, wholeCaret - removed), whole.length)
      : whole.length + 1 + (fractionCaret ?? 0)
  return { text: `${whole},${fraction}`, caret: nextCaret }
}

/**
 * Первый ввод после входа в поле — как в кассе 1С: набранные цифры заменяют
 * рубли целиком, копейки — «00» («10,00», набрали 590 → «590,00», а не «10590,00»).
 * `null` — ввод не первый по смыслу (стирание, копейки, не цифры): дальше обычная маска.
 */
export function freshPrice(raw: string, previous: string): { text: string; caret: number } | null {
  let prefix = 0
  while (prefix < raw.length && prefix < previous.length && raw[prefix] === previous[prefix]) prefix += 1
  let suffix = 0
  while (
    suffix < raw.length - prefix &&
    suffix < previous.length - prefix &&
    raw[raw.length - 1 - suffix] === previous[previous.length - 1 - suffix]
  )
    suffix += 1
  const inserted = raw.slice(prefix, raw.length - suffix)
  const comma = previous.indexOf(',')
  if (!/^\d+$/.test(inserted) || (comma >= 0 && prefix > comma)) return null
  const whole = inserted.replace(/^0+/, '') || '0'
  if (whole.length > MAX_WHOLE_DIGITS) return null
  return { text: `${whole},00`, caret: whole.length }
}
